import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

// IDI-464: the old send modal (and its PDF-only path) was deleted. Sending opens
// /change-orders/:id/review, which emails the signer the link.

// ─── CO State Machine ────────────────────────────────────────────────────────
// States: Draft → Sent → Accepted → Approved → Applied
// Simplified attestation: Accept + Approve + Apply collapsed into one step

// ─── CO Create Side-Sheet ────────────────────────────────────────────────────

// §2.1: Create modal simplified to NAME (required) + optional COMMENT.
// The comment prints on the customer CO. Line-item editing happens in the CO
// builder (clone of the quote builder), not here.
export function COCreateSheet({ open, onClose, onSubmit, job }) {
  const [form, setForm] = useState({
    name: '',
    comment: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        comment: form.comment.trim(),
        jobId: job?.id,
        status: 'draft',
      });
      onClose();
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to create change order' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 bottom-0 z-50 w-[480px] flex flex-col overflow-hidden animate-slide-in-right"
        style={{ backgroundColor: 'var(--color-surface)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Change Order</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Job Context */}
          <div className="bg-gray-50 px-4 py-3" style={{ borderRadius: 'var(--radius-md)' }}>
            <p className="text-xs font-medium text-gray-500 uppercase">Job</p>
            <p className="text-sm font-medium text-gray-900">{job?.title || 'Untitled Job'}</p>
            <p className="text-xs text-gray-500">{job?.customerName || job?.customer_name || ''}</p>
          </div>

          {/* §2.1 Name (required) */}
          <Input
            label="Change Order Name"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            placeholder="e.g., Add second-floor insulation"
            required
            autoFocus
          />

          {/* §2.1 Optional comment — prints on the customer CO */}
          <div>
            <label className="label">Comment (optional)</label>
            <textarea
              value={form.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              rows={3}
              className="input resize-none"
              placeholder="This comment prints on the customer's change order."
            />
            <p className="text-xs text-gray-400 mt-1">
              Add line items and pricing in the change-order builder after creating.
            </p>
          </div>

          {errors.submit && (
            <div className="bg-red-50 p-3 text-sm text-red-700" style={{ borderRadius: 'var(--radius-md)' }}>{errors.submit}</div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[color:var(--color-border)] px-6 py-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting}>
            Create &amp; Open Builder
          </Button>
        </div>
      </div>
    </>
  );
}
